import React, { useEffect, useRef, useState } from 'react';
import io from 'socket.io-client';
import html2canvas from 'html2canvas';
import { QRCodeCanvas } from 'qrcode.react';
import './RemoteControl.css';

let mirrorSocket;

const FRAME_INTERVAL_MS = 800;
const FRAME_SCALE = 0.3;
const FRAME_QUALITY = 0.5;

// Duplicated on purpose (kept independent from RemoteControlOverlay.js) so
// the Landing D-pad remote is never touched by this feature.
const setNativeInputValue = (el, value) => {
  const proto  = el.tagName === 'TEXTAREA' ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
  if (setter) {
    setter.call(el, value);
    el.dispatchEvent(new Event('input', { bubbles: true }));
    el.dispatchEvent(new Event('change', { bubbles: true }));
  }
};

const GlobalScreenMirror = () => {
  const [roomId]         = useState(() => Math.random().toString(36).substring(2, 8).toUpperCase());
  const [isConnected, setIsConnected] = useState(false);
  const [panelOpen, setPanelOpen]     = useState(false);

  const mousePosRef      = useRef({ x: window.innerWidth / 2, y: window.innerHeight / 2 });
  const cursorRef        = useRef(null);
  const isConnectedRef   = useRef(false);
  const frameIntervalRef = useRef(null);

  useEffect(() => { isConnectedRef.current = isConnected; }, [isConnected]);

  const initializeCursor = () => {
    const existing = document.getElementById('global-screen-mirror-cursor');
    if (existing) existing.remove();

    const cursor = document.createElement('div');
    cursor.id = 'global-screen-mirror-cursor';
    cursor.style.cssText = `
      position:fixed;width:20px;height:20px;
      border:2px solid #667eea;border-radius:50%;
      background:rgba(102,126,234,0.3);
      pointer-events:none;z-index:999999;display:none;
      transform:translate(-50%,-50%);
      transition:left 0.05s linear,top 0.05s linear;
      box-shadow:0 0 10px rgba(102,126,234,0.5);
    `;
    document.body.appendChild(cursor);
    cursorRef.current = cursor;
  };

  const removeCursor = () => {
    document.getElementById('global-screen-mirror-cursor')?.remove();
    document.body.style.cursor = 'auto';
  };

  const resetCursorPosition = () => {
    mousePosRef.current = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
    if (cursorRef.current) {
      cursorRef.current.style.left    = `${mousePosRef.current.x}px`;
      cursorRef.current.style.top     = `${mousePosRef.current.y}px`;
      cursorRef.current.style.display = 'block';
    }
  };

  const moveCursor = (deltaX, deltaY) => {
    if (!cursorRef.current) return;
    let { x, y } = mousePosRef.current;
    x = Math.max(0, Math.min(x + deltaX, window.innerWidth));
    y = Math.max(0, Math.min(y + deltaY, window.innerHeight));
    mousePosRef.current = { x, y };
    cursorRef.current.style.left    = `${x}px`;
    cursorRef.current.style.top     = `${y}px`;
    cursorRef.current.style.display = 'block';

    const el = document.elementFromPoint(x, y);
    if (el) el.dispatchEvent(new MouseEvent('mousemove', { view: window, bubbles: true, cancelable: true, clientX: x, clientY: y }));
  };

  const simulateMouseClick = (button = 'left') => {
    const active = document.activeElement;
    if (active && (
      active.tagName === 'INPUT'    ||
      active.tagName === 'TEXTAREA' ||
      active.isContentEditable
    )) return;

    const { x, y } = mousePosRef.current;
    const el = document.elementFromPoint(x, y);
    if (!el) return;

    const opts = { view: window, bubbles: true, cancelable: true, clientX: x, clientY: y, button: button === 'right' ? 2 : 0 };
    el.dispatchEvent(new MouseEvent('mousedown', opts));
    el.dispatchEvent(new MouseEvent('mouseup',   opts));
    el.dispatchEvent(new MouseEvent('click',     opts));
    if (button === 'right') el.dispatchEvent(new MouseEvent('contextmenu', opts));
  };

  const findScrollableParent = (el) => {
    while (el && el !== document.body) {
      const s = window.getComputedStyle(el);
      if ((s.overflowY === 'auto' || s.overflowY === 'scroll') && el.scrollHeight > el.clientHeight) return el;
      el = el.parentElement;
    }
    return null;
  };

  const simulateMouseScroll = (delta) => {
    const { x, y } = mousePosRef.current;
    const el = document.elementFromPoint(x, y);
    if (!el) return;
    el.dispatchEvent(new WheelEvent('wheel', { view: window, bubbles: true, cancelable: true, clientX: x, clientY: y, deltaY: delta * 100, deltaMode: 0 }));
    const scrollable = findScrollableParent(el);
    if (scrollable) scrollable.scrollBy({ top: delta * 50, behavior: 'smooth' });
  };

  const injectKey = (key) => {
    const el = document.activeElement;
    if (!el) return;

    if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA') {
      const start = el.selectionStart ?? el.value.length;
      const end   = el.selectionEnd   ?? el.value.length;
      let newVal  = el.value;

      if (key === 'Backspace') {
        newVal = start > 0 ? newVal.slice(0, start - 1) + newVal.slice(end) : newVal;
        setNativeInputValue(el, newVal);
        const pos = Math.max(0, start - 1);
        el.setSelectionRange?.(pos, pos);
      } else if (key === 'Enter') {
        const form = el.closest('form');
        if (form) form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      } else if (key.length === 1) {
        newVal = newVal.slice(0, start) + key + newVal.slice(end);
        setNativeInputValue(el, newVal);
        const pos = start + 1;
        el.setSelectionRange?.(pos, pos);
      }
    }

    el.dispatchEvent(new KeyboardEvent('keydown', { key, bubbles: true, cancelable: true }));
    el.dispatchEvent(new KeyboardEvent('keyup',   { key, bubbles: true, cancelable: true }));
  };

  const stopFrameCapture = () => {
    if (frameIntervalRef.current) {
      clearInterval(frameIntervalRef.current);
      frameIntervalRef.current = null;
    }
  };

  const startFrameCapture = () => {
    stopFrameCapture();
    const captureAndSend = () => {
      if (!isConnectedRef.current || !mirrorSocket) return;
      html2canvas(document.body, {
        scale: FRAME_SCALE,
        logging: false,
        useCORS: true,
        ignoreElements: (el) => el.id === 'global-screen-mirror-ui',
      }).then((canvas) => {
        if (!isConnectedRef.current || !mirrorSocket) return;
        const frame = canvas.toDataURL('image/jpeg', FRAME_QUALITY);
        mirrorSocket.emit('screen-frame', { roomId, frame });
      }).catch(() => {});
    };
    captureAndSend();
    frameIntervalRef.current = setInterval(captureAndSend, FRAME_INTERVAL_MS);
  };

  useEffect(() => {
    const BACKEND_URL = process.env.REACT_APP_BASE_URL || 'https://geo.geomedium.com';

    mirrorSocket = io(BACKEND_URL, {
      path: '/api/socket',
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      withCredentials: true,
    });

    mirrorSocket.on('connect', () => mirrorSocket.emit('register-host', roomId));

    mirrorSocket.on('remote-connected', () => {
      setIsConnected(true);
      initializeCursor();
      resetCursorPosition();
      document.body.style.cursor = 'none';
      startFrameCapture();
    });

    mirrorSocket.on('connection-lost', () => {
      setIsConnected(false);
      stopFrameCapture();
      removeCursor();
    });

    mirrorSocket.on('mouse-move', (data) => {
      if (!isConnectedRef.current) return;
      moveCursor(data.x, data.y);
    });
    mirrorSocket.on('mouse-click',       () => { if (isConnectedRef.current) simulateMouseClick('left');  });
    mirrorSocket.on('mouse-right-click', () => { if (isConnectedRef.current) simulateMouseClick('right'); });
    mirrorSocket.on('mouse-scroll',      (d) => { if (isConnectedRef.current) simulateMouseScroll(d.delta); });
    mirrorSocket.on('remote-key',        ({ key }) => { if (isConnectedRef.current) injectKey(key); });

    return () => {
      stopFrameCapture();
      if (mirrorSocket) mirrorSocket.disconnect();
      removeCursor();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId]);

  const mobileUrl = `${window.location.origin}/remote?roomId=${roomId}`;

  return (
    <div id="global-screen-mirror-ui" className="global-screen-mirror-ui">
      <button
        onClick={() => setPanelOpen((v) => !v)}
        title="Scan to mirror this screen on your phone"
        className={`gsm-mirror-btn${isConnected ? " is-connected" : ""}`}
      >
        <i className="fa-solid fa-qrcode" />
      </button>

      {panelOpen && (
        <div className="gsm-qr-panel">
          <div className="gsm-qr-title">
            {isConnected ? (
              <><span className="gsm-connected-dot"></span>Mobile Connected!</>
            ) : (
              'Scan QR to mirror this screen'
            )}
          </div>
          <div className="gsm-qr-code-box">
            <QRCodeCanvas value={mobileUrl} />
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalScreenMirror;
